import React from 'react';
import { PremiumLock } from '@cx-web/components/primitives/icons';
import Modal from '@cx-web/components/primitives/modal-component';
import { type MyOCData } from '@cx-web/domain-types/myoc';
import { removeRepubPrefix } from '@cx-web/utils';
import { trackEvent } from '@cx-web/utils/dataLayer';
import { formatInTimeZone } from 'date-fns-tz';

import { type Repub, REPUBS } from '@cx-modules/constants';
import { type DeviceType } from '@cx-modules/types';

import { LegacyAuthModalTrigger } from '~/components/composites/legacy-auth-modal';
import { deviceTypeConst } from '~/routes/$/constants/device-type';
import { getWebPImageUrlWithWidth } from '~/utils/getWebPImageUrlWithWidth';

import styles from './ArticleHeader.module.css';
type ArticleHeaderImage = {
  alt: string;
  url: string;
};
type ArticleHeaderAuthor = {
  fullName: string;
  id: string;
  image: ArticleHeaderImage;
  shortName: string;
};
export type ArticleHeaderProps = {
  aiGeneratedId?: string;
  authors: ArticleHeaderAuthor[];
  componentTags?: string[];
  currentPathWithRepub: string;
  description?: string;
  deviceType: DeviceType;
  image: ArticleHeaderImage;
  myocData: MyOCData;
  publishedTime?: string;
  readTime?: string;
  repub: Repub;
  title: string;
};

export const ArticleHeader = ({
  aiGeneratedId,
  authors,
  componentTags,
  currentPathWithRepub,
  description,
  deviceType,
  image,
  myocData,
  publishedTime,
  readTime,
  repub,
  title,
}: ArticleHeaderProps) => {
  const author = authors[0];
  const publishedTimeLocalised =
    publishedTime &&
    formatInTimeZone(
      publishedTime,
      REPUBS[repub].dateProperties.timezone,
      REPUBS[repub].dateProperties.format.timeDateFull,
      {
        locale: REPUBS[repub].dateProperties.locale,
      },
    );

  const isLoggedIn = !!myocData.myOCUser;

  const currentPath = removeRepubPrefix(currentPathWithRepub, repub);

  const isMobile = deviceType === deviceTypeConst.MOBILE;
  const imageWidth = isMobile ? 400 : 1000;

  const renderLoginBanner = componentTags?.includes('TIPS_PREMIUM');

  React.useEffect(() => {
    trackEvent('article_view', {
      tipsterName: author.fullName,
      articleName: title,
      isLocked: renderLoginBanner ? 'yes' : 'no',
      aiArticle: aiGeneratedId ? 'yes' : 'no',
    });
  }, [author.fullName, aiGeneratedId, renderLoginBanner, title]);

  return (
    <header
      aria-labelledby="article-title"
      className={styles.articleHeaderContainer}
      data-testid="article-header-container"
    >
      <picture className={styles.articleHeroImage}>
        <img
          alt={image.alt}
          className="w-full h-full absolute object-cover object-top"
          data-testid="article-hero-image"
          src={getWebPImageUrlWithWidth(image.url, imageWidth)}
        />
      </picture>
      <h1 className={styles.articleHeader} data-testid="article-title">
        {title}
      </h1>
      <h2 className={styles.articleSummary} data-testid="article-subtitle">
        {description}
      </h2>

      {renderLoginBanner && (
        <div className={styles.lockedContentIcon}>
          {!isLoggedIn ? (
            <Modal
              buttonComponent={
                <button className={styles.lockedContentButton}>
                  log in to view for free
                  <PremiumLock />
                </button>
              }
              onClickEvent={() => {
                trackEvent('article_header_login', {
                  tipsterName: author.fullName,
                  articleName: title,
                });
              }}
              returnButton
              transparent
            >
              <LegacyAuthModalTrigger currentPath={currentPath} myocData={myocData} viewToOpen="Login" />
            </Modal>
          ) : (
            <>unlocked</>
          )}
        </div>
      )}
      <div className={styles.articleDetailsContainer}>
        <p className={styles.articleAuthor} data-testid="article-author-name">
          {author.fullName}
          {publishedTimeLocalised && ` - ${publishedTimeLocalised}`}
        </p>
        {readTime && (
          <span className={styles.articleMetadata} data-testid="article-metadata-read-time">
            {readTime}
          </span>
        )}
      </div>
    </header>
  );
};
